import { useQuery } from "@tanstack/react-query";
import { Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { GateShell } from "@/components/app/gate";
import { Hint } from "@/components/app/layout";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { settingsQuery, tournamentQuery } from "@/lib/queries";
import { queryClient } from "@/lib/query-client";
import { isOverlayPath } from "@/lib/sw-routes";
import { rememberLastTournament, TournamentProvider } from "@/lib/tournament";
import { useT } from "../i18n";
import { applyDocumentLocale, hasDeviceChoice, rememberTournamentDefault } from "../i18n/detect";
import { switchLocale } from "../i18n/load";
import { tournamentRoute } from "../router";

/**
 * Everything under `/t/$slug` renders inside this (V2-ROADMAP.md Phase 2).
 *
 * One registry read resolves the slug to a tournament — its id (the Durable
 * Object name and API segment), its name, its dates — and provides it to the
 * tree through `TournamentProvider`. Until it resolves nothing renders at all:
 * the board fetches its bundle before painting anyway, the overlay shows
 * nothing until its socket connects, and a page drawn for the wrong event
 * would be worse than a beat of blank.
 *
 * A slug nobody registered is a page that says so, in both languages, with
 * the one way out (the front page). Not a blank board: an organiser typing a
 * link from memory needs to know the address is wrong, not the wifi.
 */
export function TournamentLayout() {
  const { slug } = tournamentRoute.useParams();
  const query = useQuery(tournamentQuery(slug));
  const tournament = query.data;
  // Three facts, not the row: the registry refetches on focus, and a fresh
  // object for the same event must not rerun this.
  const id = tournament?.id;
  const resolvedSlug = tournament?.slug;
  const name = tournament?.name;

  useEffect(() => {
    if (id === undefined || resolvedSlug === undefined || name === undefined) return;
    rememberLastTournament(resolvedSlug);
    // The tab reads as the event, not as the app. The overlay is never a
    // tab anyone reads, and never sets a locale either.
    document.title = `${name} · SCORE`;
    if (!isOverlayPath(window.location.pathname)) adoptTournamentDefault(id);
  }, [id, resolvedSlug, name]);

  if (tournament !== undefined) {
    return (
      <TournamentProvider tournament={tournament}>
        <Outlet />
      </TournamentProvider>
    );
  }
  if (query.isError) {
    const missing = query.error instanceof ApiError && query.error.status === 404;
    return <Notice missing={missing} onRetry={() => void query.refetch()} />;
  }
  return null;
}

/**
 * The tournament's own default language, for a device that has never chosen
 * one.
 *
 * Fetched THROUGH the query client rather than with a bare `fetch`, so the
 * board's own `useQuery` for settings reuses this response instead of asking
 * twice: a QR-code landing still makes one request per endpoint. It is not
 * awaited: a first-ever visitor sees Ukrainian immediately and flips only if
 * this event says English, and the answer is remembered so the flip happens at
 * most once per device.
 */
function adoptTournamentDefault(tid: string): void {
  if (hasDeviceChoice()) return;
  void queryClient
    .fetchQuery(settingsQuery(tid))
    .then(async (settings) => {
      rememberTournamentDefault(settings.locale.default);
      await switchLocale(settings.locale.default);
      applyDocumentLocale();
    })
    .catch(() => {
      /* offline, or a Worker older than this setting: Ukrainian stands */
    });
}

/** No such tournament, or no answer. Core strings only: this is the main
 *  chunk, and the staff dictionary is not loaded here. */
function Notice({ missing, onRetry }: { missing: boolean; onRetry: () => void }) {
  const t = useT();
  return (
    <GateShell eyebrow={missing ? t("gate.wrongEyebrow") : t("front.eyebrow")}>
      <p className="font-semibold text-pretty">
        {missing ? t("tournament.notFound") : t("tournament.loadFailed")}
      </p>
      {missing && <Hint>{t("tournament.notFoundHint")}</Hint>}
      {!missing && (
        <Button type="button" className="w-full" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
      <Button variant="outline" className="w-full" render={<a href="/" />}>
        {t("tournament.openList")}
      </Button>
    </GateShell>
  );
}
